import { NavLink } from 'react-router-dom';
import { Building2, Tent, Users, Wallet } from 'lucide-react';
import { cn } from '@dru/ui';
import { useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';

/**
 * The venue's own four pages — Profile, Staff, Camps, Earnings — as one row
 * of tabs over each of them, so running the venue reads as one place rather
 * than four sidebar items among the daily work (queue, samples, results).
 * Each tab follows the same permission as its sidebar item; a role that
 * holds none of the others sees no row at all.
 */
const SECTIONS = [
  { to: '/partner/profile', label: 'Profile', icon: Building2, need: 'partner.read' },
  { to: '/partner/staff', label: 'Users', icon: Users, need: 'user.read' },
  { to: '/partner/camps', label: 'Camps', icon: Tent, need: 'partner.update' },
  { to: '/partner/earnings', label: 'Earnings', icon: Wallet, need: 'settlement.read-own' },
] as const;

export function VenueSections() {
  const { permissions } = useAuth();
  const shown = SECTIONS.filter((s) => holds(permissions, s.need));
  if (shown.length < 2) return null;
  return (
    <nav aria-label="Your venue" className="-mx-1 overflow-x-auto border-b border-neutral-200">
      <ul className="flex min-w-max gap-1 px-1">
        {shown.map((s) => (
          <li key={s.to}>
            <NavLink
              to={s.to}
              className={({ isActive }) => cn(
                '-mb-px inline-flex h-10 items-center gap-1.5 border-b-2 px-3 text-small font-medium transition-colors duration-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                isActive
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-neutral-600 hover:border-neutral-300 hover:text-neutral-900',
              )}
            >
              <s.icon aria-hidden className="h-4 w-4" />
              {s.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
